import {
  CopyOutlined,
  PhoneOutlined,
  SearchOutlined,
  UserOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Empty,
  Input,
  Radio,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { apiRequests } from "src/shared/api/api";
import { CreateSegments } from "src/features/Modal";

const { Text, Title } = Typography;
const { Search } = Input;

const SegmentsDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  const [segmentDetails, setSegmentDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("name");
  const [filteredContragents, setFilteredContragents] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
  });

  // states for edit modal (same modal as in Segments.jsx)
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editSegment, setEditSegment] = useState(null);

  const getSegmentDetails = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const res = await apiRequests.segments.getById(id);
      setSegmentDetails(res.data);
      setFilteredContragents(res.data.contragents || []);
    } catch (error) {
      message.error("Ошибка загрузки данных сегмента");
      console.error("Segment details error:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Фильтрация контрагентов с задержкой
  const filterContragents = useCallback(() => {
    debounce((term, type, contragents) => {
      if (!term.trim()) {
        setFilteredContragents(contragents || []);
        return;
      }

      const filtered = contragents.filter((item) => {
        if (type === "name") {
          return item.name?.toLowerCase().includes(term.toLowerCase());
        } else {
          return item.phone?.includes(term);
        }
      });

      setFilteredContragents(filtered);
    }, 300);
  }, []);

  useEffect(() => {
    if (segmentDetails?.contragents) {
      filterContragents(searchTerm, searchType, segmentDetails.contragents);
    }
  }, [searchTerm, searchType, segmentDetails, filterContragents]);

  useEffect(() => {
    getSegmentDetails();
    setSearchTerm("");
    setSearchType("name");
  }, [getSegmentDetails]);
  // Синхронизация modal <-> URL (используем тот же роутер как в Segments)
  // Когда открываем modal для редактирования с details-страницы — нужно получить тот же полный объект сегмента,
  // что и на странице списка (включая `name`, `criteria`, `actions`, и т.д.). Иногда `getById` возвращает только укороченную версию,
  // поэтому пытаемся получить полный объект через getData({id}), а если и это не сработает — делаем запасной вариант.
  const fetchFullSegmentForEdit = useCallback(
    async (segmentId) => {
      if (!segmentId) return null;

      // 1) Сначала пробуем getById — он возвращает объект по id (даже если иногда не содержит всех вложенных полей).
      //    Если объект содержит основные поля — возвращаем его сразу.
      let fallbackCandidate = null;
      try {
        const byIdRes = await apiRequests.segments.getById(segmentId);
        if (byIdRes?.data) {
          const candidate = byIdRes.data;
          if (candidate.name || candidate.criteria || candidate.actions) {
            return candidate;
          }
          // сохраним как запасной вариант
          fallbackCandidate = candidate;
        }
      } catch (e) {
        console.warn("fetchFullSegmentForEdit: getById failed", e);
      }

      // 2) Попробуем получить весь список и найти полную запись по id (некоторые эндпоинты возвращают расширенные поля в списке)
      try {
        const allRes = await apiRequests.segments.getData();
        if (allRes?.data && Array.isArray(allRes.data)) {
          const found = allRes.data.find((s) => String(s.id) === String(segmentId));
          if (found) return found;
        }
      } catch (e) {
        console.warn("fetchFullSegmentForEdit: getData all failed", e);
      }

      // 3) Вернём запасной кандидат (если был)
      if (fallbackCandidate) return fallbackCandidate;

      return null;
    },
    []
  );

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const edit = sp.get("edit");

    if (edit && segmentDetails) {
      // если в URL есть edit - получаем *полный* объект сегмента и открываем модал
      (async () => {
        const full = await fetchFullSegmentForEdit(segmentDetails.id);
        if (full) {
          setEditSegment(full);
        } else {
          // если не удалось получить расширенный объект — используем текущие детали как запасной вариант
          setEditSegment(segmentDetails);
        }
        setEditModalVisible(true);
      })();
    } else {
      // иначе закрываем
      setEditModalVisible(false);
      // не обнуляем editSegment здесь — закрыEditAndCleanUrl кладёт в null, чтобы форма могла корректно прочитать initialValues при закрытии
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, segmentDetails, fetchFullSegmentForEdit]);

  const openEditViaRouter = () => {
    const sp = new URLSearchParams(location.search);
    if (token) sp.set("token", token);
    sp.set("edit", "1");
    navigate(`${location.pathname}${sp.toString() ? `?${sp.toString()}` : ""}`);
    // Ничего не фетчим здесь — эффект по URL займётся загрузкой один раз
  };

  const closeEditAndCleanUrl = () => {
    const sp = new URLSearchParams(location.search);
    sp.delete("edit");
    // сохраняем token, если он был
    const searchString = sp.toString();
    navigate(`${location.pathname}${searchString ? `?${searchString}` : ""}`, { replace: true });
    setEditModalVisible(false);
    setEditSegment(null);
  };


  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Скопировано в буфер обмена");
  };

  // Определение колонок для таблицы
  const columns = [
    {
      title: "Контрагент",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <Space>
          <UserOutlined style={{ color: "#1890ff" }} />
          <Text strong>{text}</Text>
        </Space>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Телефон",
      dataIndex: "phone",
      key: "phone",
      render: (text) => (
        <Space>
          <PhoneOutlined style={{ color: "#52c41a" }} />
          <Text>{text}</Text>
        </Space>
      ),
      sorter: (a, b) => a.phone.localeCompare(b.phone),
    },
    {
      title: "Действия",
      key: "actions",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Копировать контакт">
          <Button
            shape="circle"
            icon={<CopyOutlined />}
            size="small"
            onClick={() => copyToClipboard(`${record.name}: ${record.phone}`)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Skeleton loading={loading} active>
        {segmentDetails && (
          <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <Title level={2} style={{ marginBottom: 8 }}>
                {segmentDetails.name}
                {segmentDetails.is_archived && (
                  <Tag color="orange" style={{ marginLeft: 12 }}>
                    Архивный
                  </Tag>
                )}
              </Title>

              <Space size="large">
                <Text>
                  <Text strong>Контрагентов:</Text>{" "}
                  {segmentDetails.contragents?.length || 0}
                </Text>
                {segmentDetails.type_of_update === "cron" && (
                  <Text>
                    <Text strong>Интервал:</Text>{" "}
                    {segmentDetails.update_settings?.interval_minutes} мин.
                  </Text>
                )}
              </Space>
            </div>

            {/* кнопка редактирования — открывает тот же CreateSegments модал, что и на странице списка */}
            <div>
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  // открываем modal через роутер (как в Segments)
                  openEditViaRouter();
                }}
                disabled={!segmentDetails}
              >
                Редактировать
              </Button>
            </div>
          </div>
        )}

        <Card
          title={
            <Text strong style={{ fontSize: 18 }}>
              Список контрагентов
            </Text>
          }
          extra={
            segmentDetails?.contragents?.length > 0 && (
              <Button
                icon={<CopyOutlined />}
                onClick={() => {
                  const text = segmentDetails.contragents
                    .map((item) => `${item.name}: ${item.phone}`)
                    .join("");
                  copyToClipboard(text);
                }}
              >
                Скопировать всех
              </Button>
            )
          }
          bodyStyle={{ padding: "16px 0 0 0" }}
        >
          {/* Панель поиска */}
          <div style={{ padding: "0 24px 16px 24px" }}>
            <Search
              placeholder={`Поиск по ${
                searchType === "name" ? "имени" : "номеру телефона"
              }...`}
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              enterButton="Найти"
              style={{ marginBottom: 12 }}
            />

            <Radio.Group
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <Radio.Button value="name">По имени</Radio.Button>
              <Radio.Button value="phone">По номеру</Radio.Button>
            </Radio.Group>
          </div>

          {filteredContragents.length > 0 ? (
            <Table
              columns={columns}
              dataSource={filteredContragents.map((item, index) => ({
                ...item,
                key: index,
              }))}
              pagination={{
                ...pagination,
                total: filteredContragents.length,
                showTotal: (total) => `Всего: ${total} контрагентов`,
              }}
              onChange={(pagination) => setPagination(pagination)}
              scroll={{ x: 600 }}
              size="middle"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Нет данных для отображения"
                  />
                ),
              }}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary">
                  {searchTerm
                    ? `Ничего не найдено по запросу "${searchTerm}"`
                    : "В этом сегменте пока нет контрагентов"}
                </Text>
              }
              style={{ padding: "40px 0" }}
            />
          )}
        </Card>
      </Skeleton>

      {/* Тот самый CreateSegments модал — использую тот же интерфейс, что и в Segments.jsx */}
      <CreateSegments
        key={`edit-${editSegment?.id ?? 'new'}`}
        visible={editModalVisible}
        setVisible={(val) => {
          if (!val) {
            closeEditAndCleanUrl();
          } else {
            // если кто-то открывает модал вручную, синхронизируем URL
            const sp = new URLSearchParams(location.search);
            sp.set("edit", "1");
            navigate(`${location.pathname}${sp.toString() ? `?${sp.toString()}` : ""}`);
            setEditModalVisible(true);
          }
        }}
        onSuccess={() => {
          // после успешного сохранения обновляем данные и закрываем modal + чистим URL
          getSegmentDetails();
          closeEditAndCleanUrl();
        }}
        initialValues={editSegment}
        editId={editSegment?.id}
        editMode={true}
      />
    </div>
  );
};

export default SegmentsDetails;
